import React from "react";

import type { LocalizationKey } from "@/main/util/LocalizationUtil";
import type { Localizer } from "@/main/react/Types";

const slotSentinel = (index: number): string => `\u2063member-name-${index}\u2063`;
const ANY_SLOT_SENTINEL = /\u2063member-name-(\d+)\u2063/;

/** A slot that holds our own localized copy (`chat.you`), not a member's name: rendered unmarked. */
export interface OwnCopySlot {
    ownCopy: string;
}

/**
 * One interpolation slot: a member's name (marked `translate="no"`), or our own copy declared as such.
 * The KIND decides, never string equality, because a member may legitimately be called "You".
 */
export type MemberNameSlot = string | OwnCopySlot;

/**
 * A localized string with a member's name interpolated into it, rendered so the NAME is exempt from browser
 * translation and the COPY around it is not (FI-040, 2026-09-15).
 *
 * <p>`{localizer("feed.card.because_you_follow", name)}` yields one text node in which our copy and the
 * member's name cannot be told apart: `translate="no"` on the element freezes the copy for readers outside
 * our locales, and leaving it off lets a translator rewrite the name (`Dei` -> `Dios`, 2026-09-03). So the
 * key is formatted with a sentinel in the name's slot, the result is split around the sentinel, and the name
 * is rendered in its own `<span translate="no">` between the copy fragments. A fragment, so it drops into any
 * `ReactNode` position: a title prop, a dialog message, a span.
 *
 * <p>A name that is itself our own copy (`getDisplayUsername`'s `literal.deleted_user` fallback) is left
 * unmarked: marking it would freeze English for the viewer the fallback exists for.
 *
 * <p>Design: designs/01-now/2026-09-03-member-display-names-excluded-from-browser-translation-design.md §6.
 */
export function localizeWithMemberName(localizer: Localizer, key: LocalizationKey, name: string): React.ReactElement {
    return localizeWithMemberNames(localizer, key, [name]);
}

/**
 * The N-slot form (FQ-4, 2026-09-21): `{0}` … `{n-1}` each receive one {@link MemberNameSlot}. Every slot gets its
 * own indexed sentinel and the formatted string is split on all of them, so a locale that reorders the
 * placeholders still lands each name in its own span, in the order the sentence reads.
 */
export function localizeWithMemberNames(
    localizer: Localizer,
    key: LocalizationKey,
    slots: readonly MemberNameSlot[],
): React.ReactElement {
    const values = slots.map((slot) => (typeof slot === "string" ? slot : slot.ownCopy));
    const formatted = localizer(key, ...slots.map((_, index) => slotSentinel(index)));
    if (!ANY_SLOT_SENTINEL.test(formatted)) {
        // The key has no slot for any name (or the catalog dropped them): nothing to exempt.
        return <>{localizer(key, ...values)}</>;
    }
    const deletedUserCopy = localizer("literal.deleted_user");
    const nodeFor = (index: number): React.ReactNode => {
        const slot = slots[index];
        if (typeof slot !== "string") return slot.ownCopy;
        return slot === deletedUserCopy ? slot : <span translate="no">{slot}</span>;
    };
    // `split` with a capturing group interleaves copy fragments (even positions) with slot indices (odd).
    const pieces = formatted.split(new RegExp(ANY_SLOT_SENTINEL.source));
    return (
        <>
            {pieces.map((piece, position) => (
                <React.Fragment key={position}>{position % 2 === 0 ? piece : nodeFor(Number(piece))}</React.Fragment>
            ))}
        </>
    );
}
